import { useCallback, useEffect, useRef, useState } from 'react';
import styles from 'ui-components/Carousel.scss';

var onDragStart = function onDragStart(e) {
  return e.preventDefault();
};

/**
 * Creates a state backed by a ref, the ref is supposed to be async read only
 * and any updates should be done through the set callback which will force a
 * rerender of the component.
 */
var useRefState = function useRefState(def) {
  var ref = useRef(def);
  var _useState = useState(def),
    forceRerender = _useState[1];
  var setState = useCallback(function (newData) {
    ref.current = newData;
    forceRerender(newData);
  }, [ref, forceRerender]);
  return [ref, setState];
};
var useTouchCallbacks = function useTouchCallbacks(_ref) {
  var _ref$onTouchStart = _ref.onTouchStart,
    onTouchStartCb = _ref$onTouchStart === void 0 ? null : _ref$onTouchStart,
    _ref$onTouchEnd = _ref.onTouchEnd,
    onTouchEndCb = _ref$onTouchEnd === void 0 ? null : _ref$onTouchEnd;
  var _useRefState = useRefState(null),
    touchStart = _useRefState[0],
    setTouchStart = _useRefState[1];
  var _useRefState2 = useRefState(null),
    touchCurrent = _useRefState2[0],
    setTouchCurrent = _useRefState2[1];
  var resetTouch = useCallback(function () {
    setTouchStart(null);
    setTouchCurrent(null);
  }, [setTouchCurrent, setTouchStart]);
  var onTouchStart = useCallback(function (e) {
    var touch = getEventTouch(e.nativeEvent);
    if (typeof e.button === "undefined" || e.button === 0) {
      if (touch && onTouchStartCb) {
        onTouchStartCb(touch);
      }
      setTouchStart(touch);
      setTouchCurrent(touch);
    }
  }, [setTouchCurrent, setTouchStart, onTouchStartCb]);
  var onTouchMove = useCallback(function (e) {
    if (touchStart === null) {
      return;
    }
    setTouchCurrent(getEventTouch(e.nativeEvent));
  }, [touchStart, setTouchCurrent]);
  var onClickCapture = useCallback(function (e) {
    var start = touchStart.current;
    var current = touchCurrent.current;
    if (start && current && start.x !== current.x) {
      e.stopPropagation();
      e.preventDefault();
    }
    resetTouch();
  }, [touchStart, touchCurrent, resetTouch]);
  useEffect(function () {
    var onTouchEnd = function onTouchEnd() {
      var start = touchStart.current;
      var current = touchCurrent.current;
      if (start === null) {
        return;
      }
      if (start && current && onTouchEndCb) {
        // Defer call to ensure click event handler happens before any
        // sideeffects caused by the callback.
        setTimeout(function () {
          return onTouchEndCb(start, current);
        }, 0);
      }
      setTimeout(function () {
        resetTouch();
      }, 0);
    };
    window.addEventListener("touchend", onTouchEnd, true);
    window.addEventListener("mouseup", onTouchEnd, true);
    return function () {
      window.removeEventListener("touchend", onTouchEnd, true);
      window.removeEventListener("mouseup", onTouchEnd, true);
    };
  }, [touchStart, touchCurrent, onTouchEndCb]);
  return {
    touchStart: touchStart.current,
    touchCurrent: touchCurrent.current,
    resetTouch: resetTouch,
    touchCallbacks: {
      // Capture to prevent clicks on nested elements if we drag
      onClickCapture: onClickCapture,
      onDragStart: onDragStart,
      onMouseDown: onTouchStart,
      onMouseMove: onTouchMove,
      onTouchMove: onTouchMove,
      onTouchStart: onTouchStart
    }
  };
};
var slideDefaultStyle = function slideDefaultStyle(_ref2) {
  var slideWidth = _ref2.slideWidth;
  return {
    float: "left",
    display: "flex",
    flexDirection: "column",
    width: slideWidth + "px"
  };
};
var slideInnerStyle = function slideInnerStyle(_ref3) {
  var slideHeight = _ref3.slideHeight;
  return {
    minHeight: slideHeight + "px",
    position: "relative",
    display: "flex",
    flexDirection: "column"
  };
};

// @TODO: whats the difference track and frame?
var trackDefaultStyle = function trackDefaultStyle(_ref4) {
  var currentSlide = _ref4.currentSlide,
    slideWidth = _ref4.slideWidth,
    trackWidth = _ref4.trackWidth,
    dragDelta = _ref4.dragDelta;
  var pos = -(currentSlide * slideWidth) + dragDelta;
  return {
    transition: dragDelta ? "" : styles.transition,
    width: trackWidth + "px",
    transform: "translateX(" + pos + "px)"
  };
};
var frameDefaultStyle = function frameDefaultStyle(_ref5) {
  var frameWidth = _ref5.frameWidth;
  return {
    maxWidth: frameWidth,
    overflowX: "hidden",
    transform: "translateZ(0)"
  };
};
var getEventTouch = function getEventTouch(event) {
  if (event instanceof MouseEvent) {
    return {
      x: event.clientX,
      y: event.clientY
    };
  }
  if (event.touches.length === 0) {
    return null;
  }
  var ev = event.touches.item(0);
  if (ev) {
    return {
      x: ev.clientX,
      y: ev.clientY
    };
  }
};

export { frameDefaultStyle, getEventTouch, slideDefaultStyle, slideInnerStyle, trackDefaultStyle, useRefState, useTouchCallbacks };
//# sourceMappingURL=utils.js.map
